// app/_components/ClientNav.tsx
"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import NotificationBell from "./NotificationBell";

type RRRole = "Coach" | "Parent" | "Athlete" | "Admin";

function normalizeRole(raw: unknown): RRRole {
  const r = String(raw || "").trim().toLowerCase();

  if (r === "coach") return "Coach";
  if (r === "athlete") return "Athlete";
  if (r === "admin") return "Admin";

  return "Parent";
}

export default function ClientNav() {
  const pathname = usePathname();
  const { data: session, status } = useSession();

  const [localRole, setLocalRole] = useState<RRRole | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    try {
      const raw = localStorage.getItem("rr_user");

      if (!raw) {
        setLocalRole(null);
        return;
      }

      const parsed = JSON.parse(raw);

      const role = parsed?.role
        ? normalizeRole(parsed.role)
        : null;

      setLocalRole(role);
    } catch {
      setLocalRole(null);
    }
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    if (status !== "authenticated") return;

    const u = session?.user as any;

    if (!u?.id) return;

    const saved = {
      id: Number(u.id),
      email: u.email ?? null,
      name: u.name ?? null,
      role: normalizeRole(u.role),
    };

    try {
      localStorage.setItem(
        "rr_user",
        JSON.stringify(saved)
      );

      setLocalRole(saved.role);
    } catch {
      // ignore
    }
  }, [mounted, status, session]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const hideNav =
    pathname === "/login" ||
    pathname === "/create-account";

  if (hideNav) return null;

  const isHome = pathname === "/";

  const roleFromSession =
    status === "authenticated"
      ? normalizeRole((session?.user as any)?.role)
      : null;

  const role: RRRole | null =
    roleFromSession ?? localRole ?? null;

  const showAuthedUI =
    status === "authenticated" || !!localRole;

  const dashHref =
    role === "Coach"
      ? "/coach"
      : role === "Parent"
      ? "/parent"
      : role === "Athlete"
      ? "/athlete"
      : role === "Admin"
      ? "/admin"
      : "/login";

  /*
   * IMPORTANT:
   * Explicitly build the contact URL as a string.
   *
   * Example:
   * /events -> /contact?from=%2Fevents
   */
  const contactHref =
    `/contact?from=${encodeURIComponent(pathname)}` as any;

  const closeMenu = () => {
    setMenuOpen(false);
  };

  async function handleLogout() {
    try {
      localStorage.removeItem("rr_user");
      localStorage.removeItem(
        "rr_selected_wrestler_id"
      );
    } catch {
      // ignore
    }

    await signOut({
      callbackUrl: "/login",
    });
  }

  if (!mounted) return null;

  return (
    <header className="sticky top-0 z-50 border-b border-slate-800 bg-slate-950/80 backdrop-blur">
      <nav className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6">
        {/* Logo */}
        <Link
          href="/"
          className="mr-auto flex min-w-0 items-center gap-3"
          aria-label="Ready Roster home"
          onClick={closeMenu}
        >
          <Image
            src="/rr-icon-white.png"
            alt="Ready Roster"
            width={28}
            height={28}
            priority
            unoptimized
          />

          <span className="whitespace-nowrap text-lg font-bold">
            Ready Roster
          </span>
        </Link>

        {/* Desktop homepage links */}
        {isHome && (
          <div className="hidden items-center gap-6 text-sm text-slate-200 md:flex">
            <a
              href="#features"
              className="transition hover:text-red-400"
            >
              Features
            </a>

            <a
              href="#how"
              className="transition hover:text-red-400"
            >
              How it Works
            </a>

            <a
              href="#faq"
              className="transition hover:text-red-400"
            >
              FAQ
            </a>
          </div>
        )}

        {/* Desktop navigation */}
        <div className="hidden items-center gap-3 md:flex">
          <Link
            href={contactHref}
            className="rounded-lg border border-white bg-white px-3 py-2 text-sm font-semibold text-slate-950 transition hover:bg-slate-200"
          >
            Questions?
          </Link>

          {showAuthedUI ? (
            <>
              <NotificationBell />

              <Link
                href={dashHref as any}
                className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-200"
              >
                Dashboard
              </Link>

              <button
                type="button"
                onClick={handleLogout}
                className="rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-700"
              >
                Log out
              </button>
            </>
          ) : (
            <>
              <Link
                href="/login"
                className="rounded-lg border border-slate-700/70 bg-slate-800 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-700"
              >
                Log In
              </Link>

              <Link
                href="/create-account"
                className="rounded-lg bg-gradient-to-b from-[#ff3b3b] to-[#e31d2d] px-3 py-2 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
              >
                Get Started
              </Link>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white md:hidden"
          aria-label="Open menu"
          aria-expanded={menuOpen}
          onClick={() =>
            setMenuOpen((current) => !current)
          }
        >
          {menuOpen ? "Close" : "Menu"}
        </button>
      </nav>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="border-t border-slate-800 bg-slate-950/95 md:hidden">
          <div className="mx-auto max-w-6xl space-y-3 px-4 py-3 text-sm">
            {isHome && (
              <div className="grid grid-cols-2 gap-2 text-slate-200">
                <a
                  href="#features"
                  onClick={closeMenu}
                  className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2"
                >
                  Features
                </a>

                <a
                  href="#how"
                  onClick={closeMenu}
                  className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2"
                >
                  How it Works
                </a>

                <a
                  href="#faq"
                  onClick={closeMenu}
                  className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2"
                >
                  FAQ
                </a>
              </div>
            )}

            <Link
              href={contactHref}
              onClick={closeMenu}
              className="block w-full rounded-lg border border-white bg-white px-3 py-2 text-center font-semibold text-slate-950 transition hover:bg-slate-200"
            >
              Questions?
            </Link>

            {showAuthedUI && (
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-300">
                  Notifications
                </div>

                <NotificationBell />
              </div>
            )}

            <div className="flex gap-2">
              {showAuthedUI ? (
                <>
                  <Link
                    href={dashHref as any}
                    onClick={closeMenu}
                    className="flex-1 rounded-lg bg-white px-3 py-2 text-center font-semibold text-slate-900"
                  >
                    Dashboard
                  </Link>

                  <button
                    type="button"
                    onClick={() => {
                      closeMenu();
                      void handleLogout();
                    }}
                    className="flex-1 rounded-lg bg-slate-800 px-3 py-2 font-semibold text-white transition hover:bg-slate-700"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    href="/login"
                    onClick={closeMenu}
                    className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-center font-semibold text-white"
                  >
                    Log In
                  </Link>

                  <Link
                    href="/create-account"
                    onClick={closeMenu}
                    className="flex-1 rounded-lg bg-gradient-to-b from-[#ff3b3b] to-[#e31d2d] px-3 py-2 text-center font-semibold text-white"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}